/**
 * Configuration versionnée du prévisionnel SCC.
 *
 * Ce fichier est la **seule** source des constantes budgétaires : montants
 * votés, seuils de sécurité, barème de cotisation, bornes d'évaluation. Rien
 * de tout cela ne doit apparaître ailleurs dans le dépôt, et surtout pas dans
 * `apps/web` (spécification § 6 et § 7).
 *
 * Toute modification vaut décision de gestion : elle se relit dans l'historique
 * git et se justifie dans `docs/decisions.md`.
 */
/** Millésime du classeur de référence. */
export const VERSION_PREVISIONNEL = '2025-07 — Prévisionnel 2026-2030, version juillet 2025';
/**
 * Période couverte par le plan voté. Le vote porte sur cinq exercices : il ne
 * glisse pas d'année en année, il s'épuise.
 */
export const PLAN_VOTE = { premier: 2026, dernier: 2030 };
/**
 * Exercices couverts par la trajectoire pluriannuelle.
 *
 * Conservé pour les bornes de saisie et les tests : c'est la période du plan
 * voté. Ce qu'affiche l'application dépend en revanche de l'exercice en cours —
 * voir `exercicesTrajectoire`.
 */
export const EXERCICES_TRAJECTOIRE = [2026, 2027, 2028, 2029, 2030];
/**
 * Exercice en cours, déduit d'une date ISO.
 *
 * L'application n'est pas datée de 2026 : au 1ᵉʳ janvier 2027 elle suit
 * l'exercice 2027 sans qu'on touche au code. La date est injectée plutôt que
 * lue de l'horloge, pour que le calcul reste déterministe et testable.
 */
export function exerciceDeLaDate(iso) {
    const m = /^(\d{4})/.exec(iso);
    if (!m?.[1])
        throw new RangeError(`date invalide : ${iso}`);
    return Number(m[1]);
}
/**
 * Exercices que la trajectoire doit couvrir, vus depuis l'exercice en cours.
 *
 * Tant qu'on est dans la période votée, la trajectoire va de l'exercice en
 * cours au dernier exercice du plan : en 2027 elle porte 2027-2030, pas
 * 2026-2030 — l'exercice clos n'est plus une projection.
 *
 * Une fois le plan épuisé, elle reprend une fenêtre de même longueur à partir
 * de l'exercice en cours. Aucun vote ne la couvre alors, et l'application le
 * signale au lieu de faire comme si.
 */
export function exercicesTrajectoire(exerciceCourant) {
    const longueur = PLAN_VOTE.dernier - PLAN_VOTE.premier + 1;
    if (exerciceCourant > PLAN_VOTE.dernier) {
        return Array.from({ length: longueur }, (_, i) => exerciceCourant + i);
    }
    const depart = Math.max(exerciceCourant, PLAN_VOTE.premier);
    return Array.from({ length: PLAN_VOTE.dernier - depart + 1 }, (_, i) => depart + i);
}
/** Vrai quand l'exercice sort de la période sur laquelle le plan a été voté. */
export function horsPlanVote(exercice) {
    return exercice < PLAN_VOTE.premier || exercice > PLAN_VOTE.dernier;
}
/** Premier exercice pour lequel la base adhérents est exploitable. */
export const PREMIER_EXERCICE_ADHERENTS = 2019;
/**
 * Exercices écartés de la régression sur le produit moyen : années marquées
 * par la crise sanitaire et par l'appel d'une cotisation solidaire au taux
 * réduit (spécification § 5).
 */
export const EXERCICES_ATYPIQUES = [2020, 2021];
/**
 * Montants appelés au taux solidaire réduit de 0,1 %. Ils sont exclus du
 * calcul du produit moyen et signalés en note dans l'interface.
 */
export const COTISATION_SOLIDAIRE = {
    2020: 19549.89,
    2021: 24988.92,
};
/** Barème de cotisation. Il n'existe aucun forfait et il n'y a jamais eu de refonte. */
export const BAREME_COTISATION = {
    /** Taux de droit commun appliqué aux produits déclarés de l'exercice précédent. */
    taux: 0.002,
    /** Taux réduit applicable aux structures ultramarines. */
    tauxUltramarin: 0.001,
    /** Minimum exigible, en euros. */
    minimum: 30,
};
/**
 * États de cotisation valant « adhérent payeur ». Arbitrage de la direction :
 * la liste est fermée et ne se déduit pas.
 */
export const ETATS_PAYEURS = [
    'Payée',
    'Payée partiellement',
    'Payé - Doc. comptables en attente',
];
/**
 * Seuil de sécurité **voté**, tel qu'il figure au classeur : plancher de
 * trésorerie de 50 000 € augmenté d'un coût de rupture estimé. Série 2025 →
 * 2030.
 *
 * Il n'est plus le seuil appliqué : celui-ci se **calcule** désormais chaque
 * exercice à partir du coût de rupture réellement projeté (voir
 * `seuilAlerte`). La série votée reste ici comme référence, pour que l'écart
 * entre ce qui a été voté et ce que le calcul donne soit visible plutôt que
 * dissous. Voir `docs/decisions.md`, décisions 15 et 22.
 */
export const SEUIL_SECURITE_VOTE = {
    2025: 79714.9,
    2026: 82284,
    2027: 84854,
    2028: 87424,
    2029: 89993,
    2030: 92563.12,
};
export const PLANCHER_TRESORERIE = 50000;
/**
 * Seuil d'alerte applicable à un exercice.
 *
 *     seuil = plancher de trésorerie + coût de rupture du salarié le plus cher
 *
 * En deçà, le syndicat ne pourrait plus rompre le contrat le plus coûteux tout
 * en conservant son plancher de trésorerie. Le coût de rupture croissant avec
 * l'ancienneté et le salaire, le seuil se réévalue chaque exercice.
 */
export function seuilAlerte(coutRuptureMaximal) {
    return Math.round((PLANCHER_TRESORERIE + Math.max(0, coutRuptureMaximal)) * 100) / 100;
}
/**
 * Délai minimal, en mois, entre la décision de gestion et le franchissement du
 * seuil d'alerte. Redresser une trajectoire — renégocier une convention,
 * arbitrer une enveloppe, ouvrir une procédure — ne se fait pas en quinze
 * jours : la décision doit précéder le franchissement d'au moins deux
 * trimestres.
 */
export const PREAVIS_DECISION_MOIS = 6;
/** Convention Audiens : 5 000 € par an, dernière année d'application 2027. */
export const AUDIENS = {
    montantAnnuel: 5000,
    derniereAnnee: 2027,
};
/**
 * Loyer annuel du siège : 8 900 € en 2026, revalorisé chaque année au taux de
 * progression des charges indexées (décision 44).
 *
 * C'est un montant de bail, connu : il ne se déduit pas de la répartition de
 * l'enveloppe votée au prorata du réalisé, qui lui attribuait 18 072 € en 2026.
 */
export const LOYER_SIEGE = {
    montantAnnuel: 8900,
    exercice: 2026,
};
/** Loyer annuel du siège pour un exercice, revalorisé depuis `LOYER_SIEGE.exercice`. */
export function loyerSiege(exercice, progression) {
    const rang = Math.max(0, exercice - LOYER_SIEGE.exercice);
    return Math.round(LOYER_SIEGE.montantAnnuel * (1 + progression) ** rang * 100) / 100;
}
/**
 * Prévisionnels votés, par exercice.
 *
 * Seul 2026 a été voté à ce jour. Ajouter un exercice ici est la seule chose à
 * faire pour que l'application le prenne comme référence : c'est une décision
 * de gestion, et elle se relit dans l'historique.
 */
export const PREVISIONNELS_VOTES = {
    2026: {
        exercice: 2026,
        produits: {
            cotisations: 91959.24,
            fcap_ccneac: 38000,
            fcap_ccnsvp: 14000,
            dgca_professionnalisation: 30000,
            dgca_theatre: 20000,
            audiens: 5000,
        },
        charges: {
            personnel: 152728.86,
            autres: 62239.35,
        },
    },
};
/** Le prévisionnel voté d'un exercice, `null` s'il n'a pas été voté. */
export function previsionnelVote(exercice) {
    return PREVISIONNELS_VOTES[exercice] ?? null;
}
/**
 * Prévisionnel de référence d'un exercice, voté ou reconduit.
 *
 * Faute de vote, l'application **reconduit à l'identique** le dernier
 * prévisionnel voté et le signale. Elle ne l'indexe pas : inventer une
 * progression reviendrait à voter à la place du Conseil national. La mention
 * `reconduit` accompagne tout chiffre qui en dépend.
 */
export function previsionnelApplicable(exercice) {
    const vote = previsionnelVote(exercice);
    if (vote)
        return { previsionnel: vote, reconduit: false, exerciceSource: exercice };
    const votes = Object.keys(PREVISIONNELS_VOTES).map(Number).sort((a, b) => a - b);
    // Le dernier voté qui précède l'exercice ; à défaut, le premier connu.
    const source = votes.filter((a) => a <= exercice).pop() ?? votes[0];
    const modele = source !== undefined ? PREVISIONNELS_VOTES[source] : undefined;
    if (source === undefined || !modele) {
        throw new RangeError('aucun prévisionnel voté en configuration');
    }
    return {
        previsionnel: { ...modele, exercice },
        reconduit: true,
        exerciceSource: source,
    };
}
/**
 * Réserves que le classeur d'origine projetait à la clôture de chaque exercice.
 * Elles servent à mesurer l'écart entre la clôture réelle et ce qui était prévu.
 */
export const RESERVES_PREVUES = {
    2025: 177180.39,
};
/** Réserve prévue au classeur à la clôture d'un exercice, `null` si inconnue. */
export function reservePrevue(exercice) {
    return RESERVES_PREVUES[exercice] ?? null;
}
/**
 * Écart de raccordement hérité des exercices antérieurs, non encore élucidé
 * avec le cabinet comptable. L'interface doit l'afficher à côté de tout
 * chiffre qui en dépend (spécification § 8).
 */
export const ECART_RACCORDEMENT = {
    montant: 17782.38,
    mention: 'Les fonds propres de départ intègrent un écart de raccordement de 17 782,38 € ' +
        'hérité des exercices antérieurs, non encore élucidé avec le cabinet comptable.',
};
/**
 * Scénarios 1 et 2 tels qu'ils figurent au classeur voté. Ils ne sont pas
 * recalculés : ce sont des hypothèses de gestion, reprises telles quelles.
 *
 * Le scénario 2 intègre la correction d'une cotisation 2029 erronée dans le
 * classeur d'origine, qui minorait les produits de 28 901 €.
 */
export const SCENARIOS_VOTES = {
    sc1: {
        libelle: 'Scénario 1 — prudent',
        adherents: [275, 297, 318, 339, 360],
        produits: [198959.24, 201350.18, 207970.34, 199192.6, 200052.71],
        charges: [214968.21, 213259.51, 219456.15, 224840.84, 229422.12],
        produitMoyen: [334.4, 317.68, 301.79, 286.7, 272.37],
    },
    sc2: {
        libelle: 'Scénario 2 — développement',
        adherents: [311, 350, 389, 428, 467],
        produits: [226232.73, 246683.14, 263133.55, 277583.96, 292034.36],
        charges: [223468.21, 213259.51, 219456.15, 224840.84, 229422.12],
        produitMoyen: [370.52, 370.52, 370.52, 370.52, 370.52],
    },
};
/**
 * Scénario 3, construit à partir des données réelles et non du classeur :
 * cible de 300 adhérents payeurs en 2030, produit moyen prolongeant la pente
 * observée hors exercices atypiques, extinction de la convention Audiens
 * après 2027.
 */
export const SCENARIO_3 = {
    libelle: 'Scénario 3 — cible 300 adhérents',
    cible2030: 300,
    /** Progression annuelle des charges retenue, en taux. */
    progressionCharges: 0.025,
    /** Ressources conventionnelles reconduites chaque année, hors Audiens. */
    ressourcesConventionnelles: {
        fcap_ccneac: 38000,
        fcap_ccnsvp: 14000,
        dgca_professionnalisation: 30000,
        dgca_theatre: 20000,
    },
};
/** Valeurs par défaut des dix curseurs du simulateur. */
export const DEFAUTS_SIMULATEUR = {
    cible: 360,
    cotisationMoyenne: 370,
    dgca: 50000,
    fcapCcneac: 38000,
    fcapCcnsvp: 14000,
    audiens: 5000,
    subventionPonctuelle: 0,
    exerciceSubvention: 2027,
    progressionCharges: 0.025,
    ajustement: 0,
};
/** Bornes des curseurs, exposées à l'interface pour éviter tout littéral au front. */
export const BORNES_SIMULATEUR = {
    cible: { min: 180, max: 500, pas: 5 },
    cotisationMoyenne: { min: 240, max: 600, pas: 5 },
    dgca: { min: 0, max: 60000, pas: 5000 },
    fcapCcneac: { min: 0, max: 60000, pas: 1000 },
    fcapCcnsvp: { min: 0, max: 40000, pas: 1000 },
    audiens: { min: 0, max: 10000, pas: 500 },
    subventionPonctuelle: { min: 0, max: 60000, pas: 1000 },
    progressionCharges: { min: 0, max: 0.05, pas: 0.001 },
    ajustement: { min: -40000, max: 40000, pas: 1000 },
};
/**
 * Bornes d'évaluation de la synthèse. Les libellés « favorable », « vigilance »
 * et « alerte » se calculent à partir de ces seuils, jamais à la main dans le
 * gabarit (spécification § 6).
 */
export const SEUILS_EVALUATION = {
    /** Écart de fonds propres constatés au prévisionnel, en euros. */
    cloture: { favorable: 0, vigilance: -10000 },
    /**
     * Écart, en points, entre le taux d'encaissement des produits et le taux de
     * décaissement des charges à la date d'arrêté.
     */
    execution: { favorable: -10, vigilance: -25 },
    /** Factures de cotisation émises rapportées à l'exercice précédent, même date. */
    appelCotisations: { favorable: 0.9, vigilance: 0.7 },
    /** Part des ressources conventionnelles et publiques déjà encaissée. */
    ressourcesConventionnelles: { favorable: 0.7, vigilance: 0.4 },
    /** Écart du résultat projeté au résultat voté, en euros. */
    resultatProjete: { favorable: 0, vigilance: -10000 },
    /** Réserve de fin d'exercice rapportée au seuil de sécurité. */
    reserve: { favorable: 2, vigilance: 1.5 },
    /** Réserve finale d'une trajectoire rapportée au seuil 2030. */
    trajectoire: { favorable: 2, vigilance: 1 },
    /**
     * Une trajectoire dont la cible d'effectifs dépasse ce multiple du record
     * historique de payeurs est classée « hypothèse haute », quel que soit son
     * résultat financier : on ne présente pas comme favorable une projection
     * qu'aucune série n'étaye. Le seuil est fixé au doublement du record.
     */
    multipleRecordHistorique: 2,
};
/** Compte bancaire d'exploitation dont les flux constituent l'exécution. */
export const COMPTE_EXPLOITATION = {
    zohoAccountId: '36898000000586044',
    libelle: 'Crédit Mutuel courant',
};
/**
 * Rattachement des mouvements bancaires aux postes du prévisionnel.
 * La clé est le `transaction_type` de Zoho Books, éventuellement affiné par le
 * code de compte de contrepartie.
 */
export const RATTACHEMENT_PRODUITS = {
    customer_payment: { poste: 'cotisations', libelle: 'Cotisations des adhérents', budgete: true },
    sales_without_invoices: { poste: 'fcap_ccneac', libelle: 'FCAP CCNEAC', budgete: true },
    expense_refund: { poste: 'remboursements_frais', libelle: 'Remboursements de frais', budgete: false },
    other_income: { poste: 'autres_produits', libelle: 'Autres produits', budgete: false },
    transfer_fund: { poste: 'remboursements_prets', libelle: 'Remboursements de prêts', budgete: false },
    deposit: { poste: 'depots_divers', libelle: 'Dépôts divers', budgete: false },
    owner_contribution: { poste: 'apports', libelle: 'Apports', budgete: false },
    interest_income: { poste: 'produits_financiers', libelle: 'Produits financiers', budgete: false },
};
/** Postes de produit ne figurant pas au prévisionnel voté. */
export const POSTES_HORS_BUDGET = [
    'remboursements_frais',
    'autres_produits',
    'remboursements_prets',
    'depots_divers',
    'apports',
    'produits_financiers',
];
/**
 * Codes de compte de contrepartie constituant les charges de personnel.
 * Le prévisionnel est établi en brut ; les décaissements sont en net augmenté
 * du prélèvement à la source, la part salariale étant incluse dans les
 * règlements d'organismes sociaux.
 *
 * `C645` n'est pas porté par le champ `offset_account_code` dans Zoho : il
 * n'apparaît qu'entre crochets dans le libellé du compte. La résolution du
 * code doit donc regarder les deux champs (voir `codeCompte`).
 */
export const COMPTES_PERSONNEL = [
    'C640001',
    'C640002',
    'C640003',
    'C645',
    'C647',
    '6411',
];
/** Sous-ensembles du personnel, pour l'affichage détaillé. */
export const REGROUPEMENTS_PERSONNEL = [
    {
        cle: 'salaires_pas',
        libelle: 'Salaires nets et prélèvement à la source',
        comptes: ['C640001', 'C640002', 'C640003', '6411'],
    },
    { cle: 'cotisations_sociales', libelle: 'Cotisations sociales', comptes: ['C645'] },
    { cle: 'cheques_dejeuner', libelle: 'Chèques déjeuner', comptes: ['C647'] },
];
/**
 * Regroupement des autres décaissements pour la lecture en séance.
 * `comptes` s'apparie sur le code résolu, `libelles` sur le libellé exact du
 * compte de contrepartie quand Zoho n'expose aucun code.
 */
export const REGROUPEMENTS_AUTRES_CHARGES = [
    {
        cle: 'location_siege',
        libelle: 'Location immobilière (siège)',
        comptes: ['C61350', 'C6135'],
        libelles: [],
    },
    {
        cle: 'abonnements',
        libelle: 'Abonnements logiciels et Zoho',
        comptes: ['C60111', 'C6011', 'C6010'],
        libelles: [],
    },
    {
        cle: 'location_instances',
        libelle: 'Location immobilière Conseil national',
        comptes: ['C61351'],
        libelles: [],
    },
    {
        cle: 'ag_receptions',
        libelle: 'Assemblée générale et réceptions',
        comptes: [],
        libelles: ['Repas'],
    },
    {
        cle: 'deplacements',
        libelle: 'Déplacements',
        comptes: ['C625000'],
        libelles: ['Frais de déplacement en train', 'Locations de voiture', 'Frais carburant, péages...'],
    },
    {
        cle: 'fournitures_site',
        libelle: 'Fournitures et site internet',
        comptes: ['C6064', 'C6264', 'C62641', 'C6262006', 'C6240002'],
        libelles: ['Hébergement internet'],
    },
];
/**
 * Comptes hors nomenclature du prévisionnel voté : leurs décaissements
 * constituent un dépassement de charges pur.
 *   — `C61`    : Movinmotion, 1 345,50 € ;
 *   — `447200` : formation continue OPCO AFDAS, 925,76 €.
 */
export const COMPTES_HORS_NOMENCLATURE = ['C61', '447200'];
/**
 * Projection de clôture de l'exercice en cours.
 *
 * Méthode : les produits sont maintenus à leur cible budgétaire ; les charges
 * valent les charges votées augmentées des dépassements constatés sur les
 * comptes hors nomenclature.
 *
 * `ecartResiduelArbitre` porte la différence, non élucidée, entre la
 * projection arbitrée en séance (217 344,30 €) et le calcul ci-dessus
 * (217 239,47 €). Elle est conservée explicitement, signalée comme anomalie
 * dans l'écran d'administration, plutôt que dissoute dans une formule
 * reconstituée après coup. Voir `docs/decisions.md`, décision 6.
 */
export const PROJECTIONS_ARBITREES = {
    2026: { ecartResiduelArbitre: 104.83 },
};
export const PROJECTION = {
    methode: 'charges votées + dépassements constatés hors nomenclature budgétaire',
    libelleEcartResiduel: "Écart non élucidé entre la projection de charges arbitrée en séance et le cumul " +
        'des dépassements constatés sur comptes hors nomenclature.',
};
/**
 * Écart résiduel arbitré d'un exercice. Nul par défaut : un exercice sans
 * arbitrage de séance n'en porte pas, et en inventer un serait une erreur.
 */
export function ecartResiduelArbitre(exercice) {
    return PROJECTIONS_ARBITREES[exercice]?.ecartResiduelArbitre ?? 0;
}
/** Ressources conventionnelles et publiques, pour le calcul du reste à recevoir. */
export const POSTES_CONVENTIONNELS = [
    'fcap_ccneac',
    'fcap_ccnsvp',
    'dgca_professionnalisation',
    'dgca_theatre',
    'audiens',
];
/** Somme des produits votés d'un exercice. */
export function totalProduitsVotes(exercice) {
    return Object.values(previsionnelApplicable(exercice).previsionnel.produits).reduce((s, v) => s + v, 0);
}
/** Somme des charges votées d'un exercice. */
export function totalChargesVotees(exercice) {
    const c = previsionnelApplicable(exercice).previsionnel.charges;
    return c.personnel + c.autres;
}
/** Résultat voté d'un exercice. */
export function resultatVote(exercice) {
    return totalProduitsVotes(exercice) - totalChargesVotees(exercice);
}
/** Somme des ressources conventionnelles et publiques votées d'un exercice. */
export function totalConventionnelVote(exercice) {
    const produits = previsionnelApplicable(exercice).previsionnel.produits;
    return POSTES_CONVENTIONNELS.reduce((s, p) => s + (produits[p] ?? 0), 0);
}
/**
 * Versements notifiés, par exercice d'encaissement (décision 42).
 *
 * Quand un financeur a notifié ce qu'il versera, la projection de clôture du
 * poste retient la somme notifiée et non plus le montant voté : le prévisionnel
 * voté reste la référence des écarts. La comptabilité de l'exercice étant tenue
 * en encaissements, une somme due au titre d'un exercice antérieur compte dans
 * l'exercice où elle est versée.
 */
export const VERSEMENTS_NOTIFIES = {
    2026: [
        {
            poste: 'dgca_professionnalisation',
            financeur: 'DGCA — sous-direction Professions de la Création',
            auTitreDe: 2026,
            montant: 28200,
            notifieLe: '2026-10-26',
            versementPrevu: '2026-12-05',
            libelleVersement: 'début décembre 2026',
        },
        {
            poste: 'dgca_theatre',
            financeur: 'DGCA — Délégation Théâtre et Arts Associés',
            auTitreDe: 2026,
            montant: 18900,
            notifieLe: '2026-10-26',
            versementPrevu: '2026-12-05',
            libelleVersement: 'début décembre 2026',
        },
        {
            poste: 'fcap_ccnsvp',
            financeur: 'FCAP CCNSVP',
            auTitreDe: 2024,
            montant: 14749.38,
            notifieLe: '2026-10-26',
            versementPrevu: '2026-11-15',
            libelleVersement: 'mi-novembre 2026',
        },
        {
            poste: 'fcap_ccnsvp',
            financeur: 'FCAP CCNSVP',
            auTitreDe: 2025,
            montant: 15915.38,
            notifieLe: '2026-10-26',
            versementPrevu: '2026-11-15',
            libelleVersement: 'mi-novembre 2026',
        },
    ],
};
/**
 * Versements notifiés d'un exercice, connus à la date d'arrêté. Une projection
 * arrêtée avant la notification ne la voit pas : elle reste rejouable.
 */
export function versementsNotifies(exercice, dateArrete) {
    return (VERSEMENTS_NOTIFIES[exercice] ?? []).filter((v) => v.notifieLe <= dateArrete);
}
/**
 * Montant projeté d'un poste conventionnel : la somme des versements notifiés
 * quand il y en a, sinon le montant voté (ou reconduit).
 */
export function conventionnelProjete(poste, exercice, dateArrete) {
    const notifies = versementsNotifies(exercice, dateArrete).filter((v) => v.poste === poste);
    if (notifies.length > 0)
        return sommeArrondie(notifies.map((v) => v.montant));
    return previsionnelApplicable(exercice).previsionnel.produits[poste] ?? 0;
}
/** Somme des ressources conventionnelles projetées d'un exercice. */
export function totalConventionnelProjete(exercice, dateArrete) {
    return sommeArrondie(POSTES_CONVENTIONNELS.map((p) => conventionnelProjete(p, exercice, dateArrete)));
}
/** Somme au centime : la configuration n'importe pas le moteur de calcul. */
function sommeArrondie(montants) {
    return Math.round(montants.reduce((s, m) => s + Math.round(m * 100), 0)) / 100;
}
// ── Paie constatée sur les bulletins (décision 45) ─────────────────────────
/**
 * Date d'adoption de l'atterrissage (décision 45). Une projection arrêtée
 * avant garde la méthode votée — charges votées plus dépassements, ressources à
 * leur cible — : l'instantané de référence et son contrôle restent rejouables.
 */
export const ATTERRISSAGE_DEPUIS = '2026-10-28';
export function atterrissageApplicable(dateArrete) {
    return dateArrete >= ATTERRISSAGE_DEPUIS;
}
/**
 * Bulletins de paie 2026, relevés sur les PDF du cabinet de paie (Drive,
 * « Suivi Paies et Salariés / 2026 / 1. Bulletins de paie »). Chaque nouveau
 * bulletin s'ajoute ici ; les mois non relevés sont projetés.
 */
export const PAIE_BULLETINS = {
    2026: [
        {
            cle: 'jean-yannis',
            nom: 'Yannis Jean',
            salaireBaseDecide: 3495.87,
            salaireBaseVerse: 3495.87,
            navigoMensuel: 0,
            bulletins: [
                { mois: '2026-01', verseLe: '2026-01-30', brut: 3436.87, cotisationsPatronales: 1444.92, titresRestaurant: 108, navigo: 0 },
                { mois: '2026-02', verseLe: '2026-02-27', brut: 3564.8, cotisationsPatronales: 1606.11, titresRestaurant: 54, navigo: 0 },
                { mois: '2026-03', verseLe: '2026-03-31', brut: 3500.56, cotisationsPatronales: 1530.65, titresRestaurant: 96, navigo: 0 },
                { mois: '2026-04', verseLe: '2026-04-30', brut: 3500.56, cotisationsPatronales: 1527.16, titresRestaurant: 78, navigo: 0 },
                { mois: '2026-05', verseLe: '2026-05-29', brut: 3497.45, cotisationsPatronales: 1524.13, titresRestaurant: 102, navigo: 0 },
                { mois: '2026-06', verseLe: '2026-06-30', brut: 3499.86, cotisationsPatronales: 1526.54, titresRestaurant: 114, navigo: 0 },
                { mois: '2026-07', verseLe: '2026-07-31', brut: 3507.82, cotisationsPatronales: 1532.23, titresRestaurant: 108, navigo: 0 },
                { mois: '2026-08', verseLe: '2026-08-31', brut: 3535.7, cotisationsPatronales: 1547.23, titresRestaurant: 66, navigo: 0 },
                { mois: '2026-09', verseLe: '2026-09-30', brut: 3495.87, cotisationsPatronales: 1522.79, titresRestaurant: 102, navigo: 0 },
            ],
        },
        {
            cle: 'pouget-ariane',
            nom: 'Ariane Pouget',
            salaireBaseDecide: 3068.49,
            salaireBaseVerse: 3068.49,
            navigoMensuel: 45.4,
            bulletins: [
                { mois: '2026-01', verseLe: '2026-01-30', brut: 3027.41, cotisationsPatronales: 1198.67, titresRestaurant: 114, navigo: 45.4 },
                { mois: '2026-02', verseLe: '2026-02-27', brut: 3114.71, cotisationsPatronales: 1340.77, titresRestaurant: 102, navigo: 45.4 },
                { mois: '2026-03', verseLe: '2026-03-31', brut: 3068.49, cotisationsPatronales: 1272.41, titresRestaurant: 126, navigo: 45.4 },
                { mois: '2026-04', verseLe: '2026-04-30', brut: 3079.29, cotisationsPatronales: 1276.6, titresRestaurant: 78, navigo: 45.4 },
                { mois: '2026-05', verseLe: '2026-05-29', brut: 3071.03, cotisationsPatronales: 1271.56, titresRestaurant: 90, navigo: 45.4 },
                { mois: '2026-06', verseLe: '2026-06-30', brut: 3094.02, cotisationsPatronales: 1285.97, titresRestaurant: 90, navigo: 45.4 },
                { mois: '2026-07', verseLe: '2026-07-31', brut: 3089.77, cotisationsPatronales: 1281.84, titresRestaurant: 84, navigo: 45.4 },
                { mois: '2026-08', verseLe: '2026-08-31', brut: 3111.04, cotisationsPatronales: 1295.62, titresRestaurant: 60, navigo: 45.4 },
                { mois: '2026-09', verseLe: '2026-09-30', brut: 3068.49, cotisationsPatronales: 1269.94, titresRestaurant: 108, navigo: 45.4 },
            ],
        },
        {
            cle: 'pautonnier-marielle',
            nom: 'Marielle Pautonnier',
            // Décidé le 18 février 2026 : 2 222,50 €. Le cabinet applique 14,6403 € ×
            // 151,67 h = 2 220,50 € ; les 2 € par mois sont rappelés.
            salaireBaseDecide: 2222.5,
            salaireBaseVerse: 2220.5,
            navigoMensuel: 45.4,
            bulletins: [
                { mois: '2026-01', verseLe: '2026-01-30', brut: 2103.03, cotisationsPatronales: 416, titresRestaurant: 96, navigo: 45.4 },
                { mois: '2026-02', verseLe: '2026-02-27', brut: 2342.52, cotisationsPatronales: 714.23, titresRestaurant: 102, navigo: 45.4 },
                { mois: '2026-03', verseLe: '2026-03-31', brut: 2220.78, cotisationsPatronales: 566.03, titresRestaurant: 114, navigo: 45.4 },
                { mois: '2026-04', verseLe: '2026-04-30', brut: 2222.49, cotisationsPatronales: 565.5, titresRestaurant: 84, navigo: 45.4 },
                { mois: '2026-05', verseLe: '2026-05-29', brut: 2221.73, cotisationsPatronales: 565.35, titresRestaurant: 108, navigo: 45.4 },
                { mois: '2026-06', verseLe: '2026-06-30', brut: 2220.5, cotisationsPatronales: 563.74, titresRestaurant: 126, navigo: 45.4 },
                { mois: '2026-07', verseLe: '2026-07-31', brut: 2220.5, cotisationsPatronales: 564.85, titresRestaurant: 126, navigo: 45.4 },
                { mois: '2026-08', verseLe: '2026-08-31', brut: 2251.84, cotisationsPatronales: 589.39, titresRestaurant: 36, navigo: 45.4 },
                { mois: '2026-09', verseLe: '2026-09-30', brut: 2220.5, cotisationsPatronales: 565.07, titresRestaurant: 108, navigo: 45.4 },
            ],
        },
    ],
};
/**
 * Éléments ponctuels restant à verser sur l'exercice. Les primes de partage de
 * la valeur et le forfait mobilités ne portent pas de cotisations patronales
 * (structure de moins de 50 salariés ; forfait sous le plafond d'exonération).
 */
export const ELEMENTS_A_VERSER = {
    2026: [
        {
            salarie: 'jean-yannis',
            poste: 'primes_partage_valeur',
            libelle: 'Prime de partage de la valeur 2026',
            montant: 2483.28,
            moisPrevu: '2026-12',
            connuLe: '2026-02-18',
            source: 'Règle du Bureau du 18 février 2026 : écart au brut virtuel (3 702,81 − 3 495,87 = 206,94 €) × 12. ' +
                'Le montant inscrit alors, 2 129,20 €, était erroné (note au Bureau du 28 octobre 2026).',
        },
        {
            salarie: 'pouget-ariane',
            poste: 'primes_partage_valeur',
            libelle: 'Prime de partage de la valeur 2026',
            montant: 536.1,
            moisPrevu: '2026-12',
            connuLe: '2026-02-18',
            source: 'Bureau du 18 février 2026 : moitié de la hausse 2026 versée en prime.',
        },
        {
            salarie: 'jean-yannis',
            poste: 'transport_salaries',
            libelle: 'Forfait mobilités durables',
            montant: 544.8,
            moisPrevu: '2026-11',
            connuLe: '2026-10-28',
            source: 'Yannis Jean, 28 octobre 2026 : même montant que le Navigo des autres salariés, 45,40 € × 12.',
        },
    ],
};
// ── Masse salariale ────────────────────────────────────────────────────────
/**
 * Taux de charges patronales utilisé quand aucune écriture de paie ne permet
 * de le constater. Sur le premier trimestre 2026, le taux constaté s'établit à
 * 37,0 % ; cette valeur de repli en est proche et l'interface signale toujours
 * qu'un taux estimé a été utilisé.
 */
export const TAUX_CHARGES_PATRONALES_REPLI = 0.37;
/** Premier exercice pour lequel la grille de salaires est exploitable. */
export const PREMIER_EXERCICE_PAIE = 2019;
/**
 * Arbitrages salariaux du bureau, courriel à l'équipe du 18 février 2026.
 *
 * La décision ne fixe pas trois taux mais **une cible et une chaîne de
 * proportions** : Marielle Pautonnier atteint 2 800 € bruts mensuels au
 * 1ᵉʳ janvier 2030 ; Ariane Pouget est augmentée de la moitié de ce taux ;
 * Yannis Jean de la moitié de celui d'Ariane Pouget, donc du quart.
 *
 * Les taux ne sont donc pas écrits ici : ils se **déduisent** de la cible et de
 * la grille réelle (voir `tauxDecisionsBureau`). Si le brut de départ change,
 * les trois taux changent avec lui, et la cible reste tenue.
 *
 * Les salariés sont désignés par leur nom de famille, qui est ce que porte la
 * décision. Un salarié absent de la chaîne reçoit `partParDefaut`.
 */
export const DECISIONS_REMUNERATION = {
    cle: 'decisions_bureau',
    libelle: 'Décisions du bureau — février 2026',
    reference: 'Courriel à l’équipe du 18 février 2026',
    /** Cible qui commande la trajectoire de toute l'équipe. */
    cible: { nom: 'PAUTONNIER', brutMensuel: 2800, exercice: 2030 },
    /** Part du taux du référent appliquée à chaque salarié. */
    parts: { PAUTONNIER: 1, POUGET: 0.5, JEAN: 0.25 },
    partParDefaut: 0.25,
    /**
     * Compléments décidés pour le seul exercice 2026, versés en prime de partage
     * de la valeur : la part d'augmentation non portée par le brut. Ils figurent
     * dans les primes ingérées ; rappelés ici pour la lecture de la décision.
     */
    primes2026: [
        { nom: 'POUGET', montant: 536.1, motif: 'part d’augmentation 2026 non portée par le brut' },
        { nom: 'JEAN', montant: 2129.2, motif: 'écart au brut virtuel de 3 702,81 €' },
    ],
    /** Autres mesures de la même décision, sans effet sur la grille. */
    autresMesures: [
        'Prise en charge employeur de la mutuelle portée de 50 % à 100 %.',
        'Cinq jours de RTT supplémentaires pour Marielle Pautonnier sur 2026.',
        'Passage souhaité de Marielle Pautonnier en cadre 4 au forfait jours, à terme.',
    ],
};
/**
 * Proposition salariale 2027 à soumettre au Bureau, préparée par Yannis Jean
 * le 28 octobre 2026.
 *
 * Même chaîne de proportions que les décisions de février 2026 : le taux de
 * Marielle Pautonnier sert de référence, Ariane Pouget en reçoit la moitié,
 * Yannis Jean la moitié de celui d'Ariane Pouget. Le taux de référence n'est
 * pas saisi : il se déduit du passage au groupe 4, échelon 1.
 *
 * Les bruts « virtuels » 2026 sont ceux qu'auraient portés les décisions de
 * février 2026 sans prime : brut réel plus la part versée en prime.
 */
export const PROPOSITION_SALARIALE_2027 = {
    exercice: 2027,
    reference: 'Proposition préparée le 28 octobre 2026, à soumettre au Bureau',
    /** Salarié dont le taux commande celui des autres. */
    referent: 'PAUTONNIER',
    salaries: [
        {
            nom: 'PAUTONNIER',
            libelle: 'Marielle Pautonnier',
            fonction: 'Salariée',
            /** Part du taux du référent. */
            part: 1,
            /** Part de la hausse portée par le brut ; le reste est versé en prime. */
            partBrut: 1,
            brut2026: 2222.5,
            brutVirtuel2026: 2222.5,
            /** Brut 2027 décidé : groupe 4, échelon 1. */
            brutCible2027: 2359.38,
            classification2027: 'Groupe 4, échelon 1',
            minimumConventionnel2027: null,
            prime2026: 0,
            /**
             * `reconduction` : prime 2026 reconduite plus la part de hausse non portée
             * par le brut ; `ecart_virtuel` : écart entre le brut virtuel augmenté et
             * le brut réel, sur douze mois.
             */
            calculPrime: 'reconduction',
            entree: null,
        },
        {
            nom: 'POUGET',
            libelle: 'Ariane Pouget',
            fonction: 'Déléguée générale',
            part: 0.5,
            partBrut: 0.5,
            brut2026: 3068.49,
            brutVirtuel2026: 3113.16,
            brutCible2027: null,
            classification2027: null,
            minimumConventionnel2027: null,
            prime2026: 536.1,
            calculPrime: 'reconduction',
            entree: '2020-01',
        },
        {
            nom: 'JEAN',
            libelle: 'Yannis Jean',
            fonction: 'Directeur',
            part: 0.25,
            partBrut: 0,
            brut2026: 3495.87,
            brutVirtuel2026: 3702.81,
            brutCible2027: null,
            classification2027: 'Cadre 1, groupe 1',
            /** Minimum conventionnel cadre 1, groupe 1, au 1ᵉʳ janvier 2027. */
            minimumConventionnel2027: 3513.35,
            /** Montant de la décision 2026 ; l'écart au brut virtuel donnait davantage. */
            prime2026: 2129.2,
            // Choix de Yannis Jean, 28 octobre 2026.
            calculPrime: 'ecart_virtuel',
            entree: '2008-06',
        },
    ],
};
/**
 * Règles de la prime de partage de la valeur applicables aux versements 2027,
 * vérifiées sur les sources officielles le 28 octobre 2026. Chaque règle porte
 * sa source : le texte est repris tel quel dans la proposition au Bureau.
 */
export const REGLES_PRIME_PARTAGE_VALEUR_2027 = {
    verifieLe: '2026-10-28',
    /** Fin du régime d'exonération fiscale et de CSG/CRDS des petites entreprises. */
    finExonerationFiscale: '2026-12-31',
    sources: [
        {
            libelle: 'Loi n° 2022-1158 du 16 août 2022, article 1er (Légifrance)',
            url: 'https://www.legifrance.gouv.fr/loda/article_lc/LEGIARTI000048488870',
        },
        {
            libelle: 'Urssaf — Prime de partage de la valeur (page mise à jour le 7 septembre 2026)',
            url: 'https://www.urssaf.fr/accueil/employeur/beneficier-exonerations/prime-partage-valeur.html',
        },
        {
            libelle: 'BOSS — Épargne salariale, chapitre 1 (version au 1ᵉʳ janvier 2026)',
            url: 'https://boss.gouv.fr/portail/accueil/autres-elements-de-remuneration/epargne-salariale.html',
        },
        {
            libelle: 'PLFSS 2027, annexe 9, article 7 (projet non adopté)',
            url: 'https://www.securite-sociale.fr/files/live/sites/SSFR/files/medias/PLFSS/2027/annexe-9.pdf',
        },
        {
            libelle: 'Assemblée nationale, 1ʳᵉ séance du 16 octobre 2026 (PLF 2027, amendements n° 263 et 3662)',
            url: 'https://www.assemblee-nationale.fr/dyn/17/comptes-rendus/seance/session-ordinaire-de-2026-2027/premiere-seance-du-vendredi-16-octobre-2026',
        },
    ],
};
/**
 * Nomenclature budgétaire de l'application.
 *
 * Elle n'est reprise ni de `budgets_annuels`, périmé, ni de `budget_lines`,
 * dont l'import est incomplet et le typage faux. Elle est bâtie sur ce qui se
 * réconcilie à la centime avec les sources : les six postes de produits votés,
 * les charges de personnel calculées par le moteur de paie, et la ventilation
 * par compte constatée dans Zoho. Voir `docs/decisions.md`, décision 12.
 */
export const NOMENCLATURE = [
    // Produits
    { code: 'cotisations', libelle: 'Cotisations des adhérents', nature: 'produit', categorie: 'Cotisations', mode: 'inducteur', inducteur: 'cotisations', ordre: 10 },
    { code: 'fcap_ccneac', libelle: 'FCAP CCNEAC', nature: 'produit', categorie: 'Ressources conventionnelles', mode: 'saisi', inducteur: 'constant', ordre: 20 },
    { code: 'fcap_ccnsvp', libelle: 'FCAP CCNSVP', nature: 'produit', categorie: 'Ressources conventionnelles', mode: 'saisi', inducteur: 'constant', ordre: 30 },
    { code: 'dgca_professionnalisation', libelle: 'Subvention DGCA Professionnalisation', nature: 'produit', categorie: 'Subventions publiques', mode: 'saisi', inducteur: 'constant', ordre: 40 },
    { code: 'dgca_theatre', libelle: 'Subvention DGCA Théâtre', nature: 'produit', categorie: 'Subventions publiques', mode: 'saisi', inducteur: 'constant', ordre: 50 },
    { code: 'audiens', libelle: 'Convention Audiens', nature: 'produit', categorie: 'Ressources conventionnelles', mode: 'saisi', inducteur: 'constant', ordre: 60 },
    // Sans base ni inducteur : nul tant qu'il n'est pas arbitré (brouillon 2027).
    { code: 'autres_subventions', libelle: 'Autres subventions et produits', nature: 'produit', categorie: 'Subventions publiques', mode: 'saisi', inducteur: 'constant', ordre: 70 },
    // Charges de personnel
    { code: 'salaires_bruts', libelle: 'Salaires bruts', nature: 'charge', categorie: 'Charges de personnel', mode: 'inducteur', inducteur: 'personnel', rattachementExecution: 'salaires_pas', ordre: 100 },
    { code: 'charges_patronales', libelle: 'Charges patronales', nature: 'charge', categorie: 'Charges de personnel', mode: 'inducteur', inducteur: 'personnel', rattachementExecution: 'cotisations_sociales', ordre: 110 },
    { code: 'titres_restaurant', libelle: 'Titres-restaurant, part employeur', nature: 'charge', categorie: 'Charges de personnel', mode: 'saisi', inducteur: 'indexe', rattachementExecution: 'cheques_dejeuner', ordre: 120 },
    // Inducteur `constant` et non `personnel` : ce dernier rendrait les charges patronales.
    { code: 'primes_partage_valeur', libelle: 'Primes de partage de la valeur', nature: 'charge', categorie: 'Charges de personnel', mode: 'saisi', inducteur: 'constant', ordre: 115 },
    { code: 'transport_salaries', libelle: 'Transport des salariés (Navigo, forfait mobilités)', nature: 'charge', categorie: 'Charges de personnel', mode: 'saisi', inducteur: 'constant', ordre: 118 },
    // Autres charges — maille constatée dans Zoho
    { code: 'location_siege', libelle: 'Location immobilière (siège)', nature: 'charge', categorie: 'Locaux', mode: 'saisi', inducteur: 'indexe', ordre: 200 },
    { code: 'location_instances', libelle: 'Location immobilière Conseil national', nature: 'charge', categorie: 'Locaux', mode: 'saisi', inducteur: 'indexe', ordre: 210 },
    { code: 'abonnements', libelle: 'Abonnements logiciels et Zoho', nature: 'charge', categorie: 'Systèmes d’information', mode: 'saisi', inducteur: 'indexe', ordre: 220 },
    { code: 'fournitures_site', libelle: 'Fournitures et site internet', nature: 'charge', categorie: 'Systèmes d’information', mode: 'saisi', inducteur: 'indexe', ordre: 230 },
    { code: 'ag_receptions', libelle: 'Assemblée générale et réceptions', nature: 'charge', categorie: 'Vie statutaire', mode: 'saisi', inducteur: 'indexe', ordre: 240 },
    { code: 'deplacements', libelle: 'Déplacements', nature: 'charge', categorie: 'Vie statutaire', mode: 'saisi', inducteur: 'indexe', ordre: 250 },
    { code: 'autres_postes', libelle: 'Autres postes', nature: 'charge', categorie: 'Autres charges', mode: 'saisi', inducteur: 'indexe', ordre: 260 },
];
/** Poste de la nomenclature, par code. */
export function poste(code) {
    return NOMENCLATURE.find((p) => p.code === code);
}
/**
 * Seuils du moteur d'alertes. Toute règle lit ses bornes ici : aucune n'est
 * écrite dans un gabarit.
 */
export const SEUILS_ALERTES = {
    /** Disponibilités projetées rapportées au plancher de trésorerie. */
    tresorerie: { vigilance: 1.5, alerte: 1 },
    /** Rythme d'appel des cotisations rapporté à l'exercice précédent. */
    rythmeAppels: { vigilance: 0.9, alerte: 0.7 },
    /** Part encaissée d'une ressource conventionnelle à la date d'arrêté. */
    ressourceConventionnelle: { vigilance: 0.5, alerte: 0.2 },
    /** Dépassement d'un poste de charge, en part du montant budgété. */
    depassementPoste: { vigilance: 1, alerte: 1.1 },
    /** Part de la masse salariale dans le total des charges. */
    partMasseSalariale: { vigilance: 0.72, alerte: 0.8 },
    /** Réserve rapportée au seuil de sécurité. */
    reserve: { vigilance: 1.5, alerte: 1 },
    /**
     * Mois restants avant l'échéance de décision. Décroissant : moins il reste
     * de temps, plus c'est grave. Deux ans pour la vigilance, un an pour
     * l'alerte — au-delà d'un exercice, un arbitrage se prépare encore.
     */
    echeanceDecisionMois: { vigilance: 24, alerte: 12 },
    /** Ancienneté des données servies, en heures. */
    fraicheurHeures: { vigilance: 48, alerte: 168 },
    /** Nombre d'anomalies d'ingestion tolérées. */
    anomalies: { vigilance: 3, alerte: 10 },
};
// ── Primes de partage de la valeur ─────────────────────────────────────────
/**
 * Prime de partage de la valeur, dite « prime Macron ».
 *
 * Exonérée de cotisations sociales patronales et salariales dans la limite
 * annuelle par bénéficiaire ci-dessous. Au-delà du plafond, la fraction
 * excédentaire est traitée comme du salaire et supporte les charges.
 *
 * Le forfait social ne s'applique qu'aux entreprises d'au moins 250 salariés :
 * le SCC n'y est pas soumis.
 */
export const PRIME_PARTAGE_VALEUR = {
    /** Plafond d'exonération de droit commun, par bénéficiaire et par an. */
    plafondExoneration: 3000,
    /** Plafond relevé, en présence d'un accord d'intéressement. */
    plafondExonerationRenforce: 6000,
    /** Vrai si le syndicat est couvert par un accord d'intéressement. */
    accordInteressement: false,
};
// ── Coût de rupture du contrat de travail ──────────────────────────────────
/**
 * Paramètres de calcul du coût d'un licenciement.
 *
 * `budget_licenciement_params` est vide dans Directus : les paramètres sont
 * donc déclarés ici, versionnés, et modifiables sans toucher au code.
 *
 * Barème légal de l'indemnité de licenciement (art. R. 1234-2 du code du
 * travail) : un quart de mois de salaire par année d'ancienneté jusqu'à dix
 * ans, un tiers de mois par année au-delà. La convention collective peut être
 * plus favorable : les coefficients sont paramétrables.
 */
export const RUPTURE_CONTRAT = {
    /** Fraction de mois par année d'ancienneté, dix premières années. */
    fractionParAnneeJusqua10: 1 / 4,
    /** Fraction de mois par année d'ancienneté, au-delà de dix ans. */
    fractionParAnneeAudela10: 1 / 3,
    /** Seuil, en années, où la fraction change. */
    seuilAnnees: 10,
    /** Ancienneté minimale ouvrant droit à indemnité, en mois. */
    ancienneteMinimaleMois: 8,
    /**
     * Durée du préavis en mois, par ancienneté. Le préavis est travaillé ou
     * indemnisé ; dans les deux cas il coûte, et il supporte les charges.
     */
    preavisMois: { moinsDe2Ans: 1, deuxAnsEtPlus: 2, cadre: 3 },
    /** Rôles réputés cadres, pour la durée de préavis. */
    rolesCadres: ['direction', 'rh_compta'],
    /**
     * L'indemnité de licenciement est exonérée de cotisations sociales dans la
     * limite de deux plafonds annuels de la sécurité sociale.
     */
    plafondExonerationIndemnite: 94200,
    /** Provision pour congés payés restant dus, en mois de salaire. */
    provisionCongesPayesMois: 0.5,
};
//# sourceMappingURL=configuration.js.map